import React, { createContext, useContext, useState } from 'react';
import type { ReactNode } from 'react';
import type { QuoteState, QuoteContextType } from '../types/quote';

const initialQuoteState: QuoteState = {
  selectedProduct: null,
  selectedModel: null,
  selectedVariant: null,
  selectedUpgrades: [],
  selectedColor: null,
  selectedDeliveryLocation: null,
  customerDetails: {
    name: '',
    email: '',
    phone: '',
  },
  currentStep: 1,
};

const QuoteContext = createContext<QuoteContextType | undefined>(undefined);

export const useQuote = () => {
  const context = useContext(QuoteContext);
  if (context === undefined) {
    throw new Error('useQuote must be used within a QuoteProvider');
  }
  return context;
};

interface QuoteProviderProps {
  children: ReactNode;
}

export const QuoteProvider: React.FC<QuoteProviderProps> = ({ children }) => {
  const [quoteState, setQuoteState] = useState<QuoteState>(initialQuoteState);

  const updateQuoteState = (updates: Partial<QuoteState>) => {
    setQuoteState(prev => ({ ...prev, ...updates }));
  };

  const updateCustomerDetails = (field: 'name' | 'email' | 'phone', value: string) => {
    setQuoteState(prev => ({
      ...prev,
      customerDetails: {
        ...prev.customerDetails,
        [field]: value
      }
    }));
  };

  const goToStep = (step: number) => {
    if (step >= 1 && step <= 6) {
      updateQuoteState({ currentStep: step });
    }
  };

  const nextStep = () => {
    if (quoteState.currentStep < 6) {
      updateQuoteState({ currentStep: quoteState.currentStep + 1 });
    }
  };

  const previousStep = () => {
    if (quoteState.currentStep > 1) {
      updateQuoteState({ currentStep: quoteState.currentStep - 1 });
    }
  };

  const calculateTotalPrice = (): number => {
    let total = 0;

    // Variant base price (this now includes the model's base price)
    if (quoteState.selectedVariant) {
      total += quoteState.selectedVariant.basePrice;
    }

    // Upgrades
    quoteState.selectedUpgrades.forEach(upgrade => {
      total += upgrade.price * upgrade.quantity;
    });

    // Delivery fee
    if (quoteState.selectedDeliveryLocation) {
      const deliveryFee = quoteState.selectedDeliveryLocation.deliveryFee;
      if (typeof deliveryFee === 'number') {
        total += deliveryFee;
      }
      // If deliveryFee is "POA", we don't add anything to the total
    }

    return total;
  };

  const resetQuote = () => {
    setQuoteState(initialQuoteState);
  };

  const resetSubsequentSteps = (fromStep: number) => {
    setQuoteState(prev => {
      const newState = { ...prev };

      // Reset steps based on which step is being changed
      if (fromStep <= 1) {
        // If product type changes, reset everything except product type
        newState.selectedModel = null;
        newState.selectedVariant = null;
        newState.selectedUpgrades = [];
        newState.selectedColor = null;
        newState.selectedDeliveryLocation = null;
      } else if (fromStep <= 2) {
        // If model changes, reset everything from step 3 onwards
        newState.selectedVariant = null;
        newState.selectedUpgrades = [];
        newState.selectedColor = null;
        newState.selectedDeliveryLocation = null;
      }

      return newState;
    });
  };

  const value: QuoteContextType = {
    quoteState,
    updateQuoteState,
    updateCustomerDetails,
    goToStep,
    nextStep,
    previousStep,
    calculateTotalPrice,
    resetQuote,
    resetSubsequentSteps,
  };

  return (
    <QuoteContext.Provider value={value}>
      {children}
    </QuoteContext.Provider>
  );
};
